/**
 * Account Triage Content
 *
 * Centralized content for the account triage form and related components.
 * Content is organized by field name for easy reference and maintainability.
 *
 * This structure provides both the new field-based organization and
 * backward compatibility with the old structure.
 */

export const AccountTriageContent = {
  // ============================================================================
  // GLOBAL CONTENT
  // ============================================================================
  global: {
    genericError: 'This field is required',
    dropdownPlaceholder: 'Please Select One',
    continueButton: 'Continue',
    transferAnotherAccountButton: 'Transfer another account',
    reviewAndSignButton: 'Review and sign',
    breadcrumb: 'Account {{number}}',
    pageTitle: "Now tell us about the {{ordinal}} account you're inheriting",
  },

  // ============================================================================
  // FIELD CONTENT
  // Organized by field name for easy lookup
  // ============================================================================
  fields: {
    accountType: {
      question: 'What type of account are you inheriting?',
      label: 'Account type',
      tooltip:
        'We recommend taking a look at a previous statement for the person who passed away. You can find the account types there.',
      tooltipId: 'accountTypeToolTip',
      errorText: 'This field is required',
    },

    relationshipType: {
      question: 'How were you named or listed on the account?',
      label: 'Relationship',
      tooltip:
        'The person who passed away managed the beneficiaries who will receive the assets. They did this by listing their name, relationship, or an executor. If they did not designate beneficiaries their assets will go to probate, and a court will determine an executor.',
      tooltipId: 'relationship',
      errorText: 'This field is required',
    },

    typeOfTrust: {
      question: 'What type of account are you inheriting into?',
      label: 'Account type',
      errorText: 'This field is required',
    },

    isOnlyExecutor: {
      question: 'Are you the only executor?',
      yesLabel: 'Yes',
      noLabel: 'No',
      errorText: 'This field is required',
    },

    isOnlyBeneficiary: {
      question: 'Are you the only listed beneficiary?',
      yesLabel: 'Yes',
      noLabel: 'No',
      errorText: 'This field is required',
    },

    isOnlyTrustee: {
      question: 'Are you the only trustee?',
      yesLabel: 'Yes',
      noLabel: 'No',
      errorText: 'This field is required',
    },

    wasMarried: {
      question: 'Was the decedent married at the time of death?',
      yesLabel: 'Yes',
      noLabel: 'No',
      errorText: 'This field is required',
    },

    isDecedentSpouse: {
      question: "Are you the decedent's spouse?",
      yesLabel: 'Yes',
      noLabel: 'No',
      errorText: 'This field is required',
    },

    wasAccountPreviouslyInherited: {
      question: 'Was the account previously inherited by the decedent?',
      yesLabel: 'Yes',
      noLabel: 'No',
      tooltip:
        'If the IRA was previously inherited by the decedent, it would be listed as an "Inherited IRA" on all of the account statements.',
      tooltipId: 'isInheritedAccountTip',
      errorText: 'This field is required',
    },

    originalDecedentName: {
      question: "What was the original decedent's name?",
      label: "Original decedent's full name",
      placeholder: 'Enter full name',
      errorText: 'This field is required',
    },

    isNotOnlyInheritingAccount: {
      question: 'Are you transferring funds from more than one account?',
      label: "I'm inheriting more than one {{type}} account",
      yesLabel: 'Yes',
      noLabel: 'No',
      errorText: 'This field is required',
    },

    isOnlyChild: {
      question: 'Are you the only child/grandchild of the account owner?',
      label: "I'm the only child/grandchild of the account owner",
      yesLabel: 'Yes',
      noLabel: 'No',
      errorText: 'This field is required',
    },

    jointAccounts: {
      question: "Select the joint account(s) you'd like to inherit",
      title: "Select the joint account(s) you'd like to inherit",
      label: 'Joint accounts',
      errorText: 'Make a selection to continue',
    },
  },

  // ============================================================================
  // LEGACY ACCESSORS (for backward compatibility)
  // Maps old structure to new structure
  // ============================================================================
  get dropdowns() {
    return {
      accountType: {
        question: AccountTriageContent.fields.accountType.question,
        label: AccountTriageContent.fields.accountType.label,
      },
      relationship: {
        question: AccountTriageContent.fields.relationshipType.question,
        label: AccountTriageContent.fields.relationshipType.label,
      },
      typeOfTrust: {
        question: AccountTriageContent.fields.typeOfTrust.question,
        label: AccountTriageContent.fields.typeOfTrust.label,
      },
    };
  },

  get radiobuttons() {
    return {
      isOnlyOneExecutor: {
        question: AccountTriageContent.fields.isOnlyExecutor.question,
        yes: AccountTriageContent.fields.isOnlyExecutor.yesLabel,
        no: AccountTriageContent.fields.isOnlyExecutor.noLabel,
      },
      isOnlyOneBeneficiary: {
        question: AccountTriageContent.fields.isOnlyBeneficiary.question,
        yes: AccountTriageContent.fields.isOnlyBeneficiary.yesLabel,
        no: AccountTriageContent.fields.isOnlyBeneficiary.noLabel,
      },
      isOnlyTrustee: {
        question: AccountTriageContent.fields.isOnlyTrustee.question,
        yes: AccountTriageContent.fields.isOnlyTrustee.yesLabel,
        no: AccountTriageContent.fields.isOnlyTrustee.noLabel,
      },
      wasMarried: {
        question: AccountTriageContent.fields.wasMarried.question,
        yes: AccountTriageContent.fields.wasMarried.yesLabel,
        no: AccountTriageContent.fields.wasMarried.noLabel,
      },
      isDecedentSpouse: {
        question: AccountTriageContent.fields.isDecedentSpouse.question,
        yes: AccountTriageContent.fields.isDecedentSpouse.yesLabel,
        no: AccountTriageContent.fields.isDecedentSpouse.noLabel,
      },
    };
  },

  get tooltips() {
    return {
      accountType: AccountTriageContent.fields.accountType.tooltip,
      relationship: AccountTriageContent.fields.relationshipType.tooltip,
      inheritedIRA: AccountTriageContent.fields.wasAccountPreviouslyInherited.tooltip,
    };
  },

  // Legacy top-level properties
  get breadcrumb() {
    return AccountTriageContent.global.breadcrumb;
  },
  get pageTitle() {
    return AccountTriageContent.global.pageTitle;
  },
  get continueButton() {
    return AccountTriageContent.global.continueButton;
  },
  get transferAnotherAccountButton() {
    return AccountTriageContent.global.transferAnotherAccountButton;
  },
  get reviewAndSignButton() {
    return AccountTriageContent.global.reviewAndSignButton;
  },
  get dropdownPlaceholderText() {
    return AccountTriageContent.global.dropdownPlaceholder;
  },
  get genericError() {
    return AccountTriageContent.global.genericError;
  },

  accountPreviouslyInheritedQuestion: 'Was the account previously inherited by the decedent?',
  decedentsNameQuestion: 'What was the original decedents name?',
  isMoreThanOneInheritingAccountCheckbox: "I'm inheriting more than one {{type}} account",
  originalDecedentNameLabel: "Original decedent's full name",
  isOnlyOneChildCheckbox: "I'm the only child/grandchild of the account owner",

  // ============================================================================
  // BANNER/MODAL CONTENT
  // ============================================================================
  estateBanner: {
    title: "You'll need to call in to complete this request",
    line1:
      "For multi-trustee and multi-executor scenarios, we need to collect additional signatures that can't be done through our online process. Give us a call at 877-662-7447 to complete this request.",
    line2:
      "If you have other accounts to inherit, select another account type in the drop down and continue, or review and sign if you're ready to submit the rest of your request.",
  },

  noDecedentSpouseBanner: {
    title: 'You’ll need to call in to complete this request',
    line1:
      'In most cases, the spouse is entitled to receive the assets prior to them going to the estate. You can either have the spouse walk through this process or give us a call at 877-662-7447 to complete this request.',
    line2:
      'If you have other accounts to inherit, select another account type in the drop down and continue, or review and sign if you’re ready to submit the rest of your request.',
  },

  estateFAQLink: {
    title: 'Need more help?',
  },

  estateFAQModal: {
    title: 'Need more help?',
    text: "We know how hard it can be to lose a loved one and are here to help streamline the process. If you have questions or need information, you can give us a call at 877-662-7447. We're available Monday to Friday, 9 a.m. to 5 p.m. EST.",
    closeButton: 'Close',
  },

  estate: {
    header: 'Estate Information',
    estateName: 'Estate name',
    estateTin: 'Estate tax identification number',
    federalTaxClassification: 'Federal tax classification',
    estateCountry: 'Country where estate was established',
    estateTaxResidency: 'Tax residency',
    estateWithholding: 'Are you subject to backup withholding?',
  },

  jointAccounts: {
    title: "Select the joint account(s) you'd like to inherit",
    errorMessage: 'Make a selection to continue',
    banner: {
      title: "We couldn't find a joint account for you to inherit",
      line1:
        "We currently only support inheritance transfers for joint accounts with two owners registered as Joint tenants with rights of survivorship (JTWROS). If you're looking to inherit a joint account with another registration type, please give us a call at 877-662-7447.",
      line2:
        "If you have other accounts to inherit, select another account type in the dropdown and continue, or review and sign if you're ready to submit the rest of your request.",
    },
  },

  receivingAccount: {
    title: 'Where would you like to transfer the investments?',
    errorMessage: 'Make a selection to continue',
    ira: {
      description:
        'You can open a <em>new</em> IRA with us, or you can move the money to an <em>eligible</em> IRA you already have open with us—whichever you prefer.',
      newHeader: 'Open a new IRA',
      newRadioText: 'I prefer to open a new IRA brokerage account',
      existingHeader: 'Select an eligible IRA',
    },
    nonRetirement: {
      description:
        'You can open a <em>new</em> individual nonretirement account with us, or you can move the money to an <em>eligible</em> individual nonretirement account you already have open with us—whichever you prefer.',
      newHeader: 'Open a new account',
      newRadioText: 'I prefer to open a new brokerage account',
      existingHeader: 'Select an eligible nonretirement account',
    },
    noEligibleAccountsDescription:
      'You can open a new account with us, or you can move the money to an eligible account you already have open with us—whichever you prefer.',
    noEligibleAccounts: {
      banner:
        "You don't have any accounts that are eligible to receive the assets, so you'll need to open a new one. We'll guide you through the process in the next step after you click continue.",
    },
  },

  assumeOrInheritTitle: 'Do you want to assume or inherit the account?',
  assumeOrInheritText:
    "As a spouse and sole beneficiary, you have the option to assume the decedent's IRA and treat it as your own.",
  assumeOrInheritLink: 'Learn more about assuming vs. inheriting',
  assumeOrInheritOptions: {
    ROTH_IRA: [
      {
        title: 'Assume',
        description: 'Transfers into your name',
        value: 'assume',
      },
      {
        title: 'Inherit',
        description: 'Transfers into an Inherited Roth IRA',
        value: 'inherit',
      },
    ],
    TRADITIONAL_IRA: [
      {
        title: 'Assume',
        description: 'Transfers into your name',
        value: 'assume',
      },
      {
        title: 'Inherit',
        description: 'Transfers into an Inherited IRA',
        value: 'inherit',
      },
    ],
  },
  assumeInheritError: 'Make a selection to continue',

  assumeOrInheritModal: {
    ROTH_IRA: {
      title: 'Assuming vs. inheriting a Roth IRA',
      description:
        "As a spouse and sole beneficiary, you have the option to assume the decedent's IRA and treat it as your own. Please note, if you choose to inherit the account now, you can still assume it later.",
      assume: {
        title: 'Assume the Roth IRA',
        subTitle: 'Transfers into your name',
        contributionsTerm: 'You <b>can</b> contribute the maximum amount each year.',
        contributionsTitle: 'Contributions',
        distributions: [
          'Distributions can be taken out at any time, penalty-free, if the 5-year holding period for Roth IRAs was met and the decedent was at least 59.5 years old.',
          'If the account has not met the 5-year holding period, withdrawals of earnings may be subject to income tax.',
        ],
        distributionsTitle: 'Distributions',
        rmd: 'There are no RMD requirements for an assumed Roth IRA.',
        rmdTitle: 'Required Minimum Distribution (RMD)',
      },
      inherit: {
        title: 'Inherit the Roth IRA',
        subTitle: 'Transfers into an inherited Roth IRA',
        contributionsTerm: "You <b>can't</b> contribute to an inherited Roth IRA.",
        contributionsTitle: 'Contributions',
        distributions: [
          'Distributions can be taken out at any time, penalty-free, if the 5-year holding period for Roth IRAs was met and the decedent was at least 59.5 years old.',
          'If the account has not met the 5-year holding period, withdrawals of earnings may be subject to income tax.',
        ],
        distributionsTitle: 'Distributions',
        rmd: "RMD requirements vary based on the decedent's age and year of passing. Please visit the IRS website to learn more about ",
        rmdTitle: 'Required Minimum Distribution (RMD)',
        rmdLinkUrl:
          'https://investor.vanguard.com/investor-resources-education/retirement/rmd-rules-for-inherited-iras',
        rmdLinkLabel: 'RMD requirements for Roth IRAs.',
      },
    },
    TRADITIONAL_IRA: {
      title: 'Assuming vs. inheriting an IRA',
      description:
        "As a spouse and sole beneficiary, you have the option to assume the decedent's IRA and treat it as your own. Please note, if you choose to inherit the account now, you can still assume it later.",
      assume: {
        title: 'Assume the IRA',
        subTitle: 'Transfers into your name',
        contributionsTerm: 'You <b>can</b> contribute the maximum amount each year.',
        contributionsTitle: 'Contributions',
        distributions: [
          'Any distributions taken before age 59.5 will be included in your taxable income and may be subject to an additional 10% tax.',
          'Once you reach age 59.5, penalty-free distributions can be taken at anytime.',
        ],
        distributionsTitle: 'Distributions',
        rmd: "You'll start taking RMDs at age 73.",
        rmdTitle: 'Required Minimum Distribution (RMD)',
      },
      inherit: {
        title: 'Inherit the IRA',
        subTitle: 'Transfers into an inherited IRA',
        contributionsTerm: "You <b>can't</b> contribute to an inherited IRA.",
        contributionsTitle: 'Contributions',
        distributions: [
          "Generally you can choose to start taking distributions December 31 of the year after the decedent's passing, or the year the decedent would've turned 73.",
          "You'll also choose to take the distributions based off your own life expectancy or by using the 5 or 10-year rule.",
        ],
        distributionsTitle: 'Distributions',
        rmd: "RMD requirements vary based on the decedent's age and year of passing. Please visit the IRS website to learn more about ",
        rmdTitle: 'Required Minimum Distribution (RMD)',
        rmdLinkUrl:
          'https://investor.vanguard.com/investor-resources-education/retirement/rmd-rules-for-inherited-iras',
        rmdLinkLabel: 'RMD requirements for IRAs.',
      },
    },
  },

  beneficiaryCard: {
    cardTitle: 'Beneficiary',
    decedentSaveButtonText: 'Save',
    decedentCancelButtonText: 'Cancel',
    addDecedentBaseInfoButtonText: 'Add another beneficiary',
    title: 'List all the other beneficiaries',
    description:
      "We'll need the names and birthdates for all the account owner's children (except you).",
    reminderTitle: 'Important reminder: ',
    reminderdescription:
      'Each beneficiary who you list here will also need to complete this same online process. To make everything as easy as possible for you, we recommend making sure all names and birthdates are exactly the same on all submissions (for example, listing John J. Doe and not simply John Doe).',
    fullLegalName: {
      reqErrorMsg: 'This field is required',
      label: 'Full legal name',
    },
    dateOfBirth: {
      reqErrorMsg: 'This field is required',
      label: 'Date of birth',
    },
    dateOfDeath: {
      doDbeforeDobErrorMsg: 'The date of death cannot be before the date of birth',
      label: 'Date of death',
    },
    notSavedErrorMsg: 'Save information entered',
    removeLabel: 'Remove',
    editLabel: 'Edit',
    hasChildrendQuestion:
      'Does this person who passed away have natural or legally adopted children?',
    reqErrorMsg: 'This field is required',
  },

  // ============================================================================
  // BACKWARD COMPATIBILITY - Additional properties
  // Note: dropdowns, radiobuttons, and tooltips are already provided by getter methods above
  // ============================================================================
  duplicateErrorMessage: {
    title: 'Error',
    description:
      "You've already entered the inheritance details for this account type and relationship, which we’ll apply to all accounts where you’re a beneficiary. Please choose a different account type or relationship, or select <b>Review and sign</b> to continue.",
  },
  duplicateModal: {
    title: 'Fix errors to continue',
    description:
      'Please fix the errors with your account and beneficiary type to continue with your request.',
    buttonLabel: 'Close',
  },
};

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** Type for field content */
export type FieldContent =
  (typeof AccountTriageContent.fields)[keyof typeof AccountTriageContent.fields];

/** Type for all content */
export type AccountTriageContentType = typeof AccountTriageContent;
